'use client'

import { useEffect, useState } from 'react'
import { sharedFetch } from '@/lib/sharedFetch'

/**
 * BuildVersion - Logs build info to console only (no UI)
 * Used for deployment verification
 */
export default function BuildVersion() {
  useEffect(() => {
    // Fetch build info and log to console
    // Add a cache-busting query param in case an intermediary caches despite headers.
    // Was `/api/build-info?t=${Date.now()}` — a per-render cache-buster, which guaranteed this and
    // useBuildInfo() below could NEVER share a cache entry, so both fetched simultaneously on every
    // page load (215ms + 212ms measured). sharedFetch dedupes them onto one request; `cache:no-store`
    // inside it still defeats intermediaries, so the buster is redundant.
    sharedFetch<{ buildId: string; buildDate: string }>('/api/build-info', 300_000)
      .then((data) => {
        if (data.buildId) {
          const shortVersion = data.buildId.split('-')[2] || data.buildId.slice(-8)
          const buildTime = new Date(data.buildDate).toLocaleTimeString('en-US', {
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
          })
          console.log(
            `%c[BUILD] Build Version: %c${shortVersion} %c* %c${buildTime}`,
            'color: #a1a1aa; font-weight: normal',
            'color: #d4d4d8; font-weight: bold; font-family: monospace',
            'color: #52525b',
            'color: #a1a1aa',
          )
          console.log(`%c[PKG] Full Build ID: %c${data.buildId}`, 'color: #a1a1aa', 'color: #d4d4d8; font-family: monospace')
          console.log(`%c[DATE] Deployed: %c${data.buildDate}`, 'color: #a1a1aa', 'color: #d4d4d8')
        }
      })
      .catch(() => {
        // Fallback: Use current timestamp
        const timestamp = Date.now()
        console.log(
          `%c[BUILD] Build Version: %clive-${timestamp}`,
          'color: #a1a1aa; font-weight: normal',
          'color: #d4d4d8; font-weight: bold; font-family: monospace',
        )
      })
  }, [])

  // No UI rendering - console only
  return null
}

/**
 * Hook to get build info for use in components
 */
export function useBuildInfo() {
  const [buildInfo, setBuildInfo] = useState<{
    buildId: string
    buildDate: string
    shortVersion: string
  } | null>(null)

  useEffect(() => {
    // Same shared request as <BuildVersion/> above. Build metadata cannot change without a reload,
    // so a 5-minute TTL is generous.
    sharedFetch<{ buildId: string; buildDate: string }>('/api/build-info', 300_000)
      .then((data) => {
        if (data.buildId) {
          const shortVersion = data.buildId.split('-')[2] || data.buildId.slice(-8)
          setBuildInfo({
            buildId: data.buildId,
            buildDate: data.buildDate,
            shortVersion,
          })
        }
      })
      .catch(() => {
        const timestamp = Date.now()
        setBuildInfo({
          buildId: `live-${timestamp}`,
          buildDate: new Date(timestamp).toISOString(),
          shortVersion: `live-${timestamp.toString().slice(-6)}`,
        })
      })
  }, [])

  return buildInfo
}

